
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  ArrowLeft, 
  Plus, 
  Kanban, 
  Calendar, 
  Users, 
  Settings,
  MoreVertical,
  Archive,
  Star,
  Clock
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useBoardContext } from '@/contexts/BoardContext';
import { useNavigate } from 'react-router-dom';
import { useCardNavigation } from '@/hooks/useCardNavigation';
import { formatDistanceToNow } from 'date-fns';

interface MobileDashboardProps {
  onClose: () => void;
}

export const MobileDashboard = ({ onClose }: MobileDashboardProps) => {
  const { boards, setCurrentBoardId, allCards, allColumns } = useBoardContext();
  const navigate = useNavigate();
  const { navigateToCard } = useCardNavigation();
  const [filter, setFilter] = useState<'all' | 'recent' | 'starred'>('all');

  const handleBoardSelect = (boardId: string) => {
    setCurrentBoardId(boardId);
    onClose();
  };

  const getBoardStats = (boardId: string) => {
    if (!allCards || !allColumns) return { totalCards: 0, completedCards: 0 };
    
    // Get columns for this board
    const boardColumns = allColumns.filter(column => column.board_id === boardId);
    const columnIds = boardColumns.map(column => column.id);
    
    // Get cards for these columns
    const boardCards = allCards.filter(card => columnIds.includes(card.column_id));
    const totalCards = boardCards.length;
    const completedCards = boardCards.filter(card => card.archived).length;
    
    return { totalCards, completedCards };
  };

  const getRecentActivity = (boardId: string) => {
    if (!allCards || !allColumns) return 'No activity';
    
    // Get columns for this board
    const boardColumns = allColumns.filter(column => column.board_id === boardId);
    const columnIds = boardColumns.map(column => column.id);
    
    // Get cards for these columns
    const boardCards = allCards.filter(card => columnIds.includes(card.column_id));
    
    // Get most recent update
    const recentCard = boardCards
      .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())[0];
    
    return recentCard ? formatDistanceToNow(new Date(recentCard.updated_at), { addSuffix: true }) : 'No activity';
  };

  const filteredBoards = boards?.filter(board => {
    switch (filter) {
      case 'recent':
        // Could implement recent boards logic here
        return true;
      case 'starred':
        // Could implement starred boards logic here
        return true;
      default:
        return true;
    }
  }) || [];

  const handleCardNavigation = (card: any) => {
    navigateToCard(card.id, card.column_id);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-900 dark:to-slate-800">
      {/* Header */}
      <div className="bg-background border-b border-border px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={onClose}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h1 className="text-xl font-bold">Dashboard</h1>
          </div>
          <Button size="sm" onClick={() => navigate('/board-settings')}>
            <Settings className="h-4 w-4 mr-2" />
            Settings
          </Button>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="p-4">
        <div className="grid grid-cols-2 gap-3 mb-6">
          <Button variant="outline" className="h-16 flex-col gap-2">
            <Plus className="h-5 w-5" />
            <span className="text-sm">New Board</span>
          </Button>
          <Button variant="outline" className="h-16 flex-col gap-2" onClick={() => navigate('/calendar')}>
            <Calendar className="h-5 w-5" />
            <span className="text-sm">Calendar</span>
          </Button>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2 mb-4">
          {(['all', 'recent', 'starred'] as const).map((f) => (
            <Button
              key={f}
              variant={filter === f ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setFilter(f)}
              className="capitalize"
            >
              {f === 'starred' && <Star className="h-3 w-3 mr-1" />}
              {f === 'recent' && <Clock className="h-3 w-3 mr-1" />}
              {f}
            </Button>
          ))}
        </div>

        {/* Boards Grid */}
        <div className="space-y-3">
          {filteredBoards.map((board) => {
            const stats = getBoardStats(board.id);
            const lastActivity = getRecentActivity(board.id);

            return (
              <Card 
                key={board.id} 
                className="cursor-pointer transition-all hover:shadow-md"
                onClick={() => handleBoardSelect(board.id)}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Kanban className="h-5 w-5" />
                        {board.title}
                      </CardTitle>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem>
                          <Star className="h-4 w-4 mr-2" />
                          Add to Favorites
                        </DropdownMenuItem>
                        <DropdownMenuItem>
                          <Users className="h-4 w-4 mr-2" />
                          Manage Members
                        </DropdownMenuItem>
                        <DropdownMenuItem>
                          <Archive className="h-4 w-4 mr-2" />
                          Archive Board
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between text-sm text-muted-foreground">
                    <div className="flex items-center gap-4">
                      <span>{stats.totalCards} cards</span>
                      {stats.completedCards > 0 && (
                        <span>{stats.completedCards} completed</span>
                      )}
                    </div>
                    <span>{lastActivity}</span>
                  </div>
                  
                  {/* Progress Bar */}
                  {stats.totalCards > 0 && (
                    <div className="mt-3">
                      <div className="w-full bg-muted rounded-full h-2">
                        <div 
                          className="bg-primary h-2 rounded-full transition-all" 
                          style={{ width: `${(stats.completedCards / stats.totalCards) * 100}%` }}
                        />
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {Math.round((stats.completedCards / stats.totalCards) * 100)}% complete
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
};
